import { IShowable } from 'Utility/SDK/Drivers/IShowable';
import { getCrossPromoRewardedVideoAsync } from 'Utility/SDK/CrossPromoRewardVideo/CrossPromoRewardedVideo';
import CrossPromoDriver, { CrossPromoAssetType, CrossPromoPlacement } from 'Utility/CrossPromo/CrossPromoDriver';
import { switchGame } from '@softgames/x-promo-game-client';
import Game from 'Game';
import { track } from 'Utility/Tracking/Track';
import { AdType } from 'Utility/Tracking/DataSet/Ad';
import { isIOS, isOSBelowVersion } from 'Utility/Platform';
import { IGameState } from 'State/Game/IGameState';
import { AdDebugMode } from 'State/Game/AdDebugMode';
import { Random } from 'Utility/Random';
import { getRewardedAdFallbackImage } from '../CrossPromoRewardVideo/CrossPromoRewardedImage';

export async function getRewardedVideo(placement: string, state: IGameState): Promise<IShowable> {
    let facebookShowable: IShowable;
    let crossPromoShowable: IShowable;

    const platformAdsDisabled: boolean = state.perSessionData.adDebugMode === AdDebugMode.FAIL_PLATFORM_ONLY;

    try {
        if (platformAdsDisabled) throw new Error('Ad platform disabled.');
        facebookShowable = await FBInstant.getRewardedVideoAsync(placement);
    } catch {
        console.log('Fetching Facebook ad failed.');
        crossPromoShowable = await getCrossPromoShowable(state);

        if (crossPromoShowable !== undefined) {
            track('adSuccess', 0);
        }
    }

    return {
        loadAsync: async () => {
            if (crossPromoShowable !== undefined) {
                track('adSuccess', 0);
                track('adType', AdType.REWARDED_CROSS_PROMO);
                return crossPromoShowable.loadAsync();
            }

            try {
                console.log('Loading Facebook ad...');
                await facebookShowable.loadAsync();
                track('adType', AdType.REWARDED);
            } catch {
                console.log('Loading Facebook ad failed.');

                track('adSuccess', 0);
                crossPromoShowable = await getCrossPromoShowable(state);
                track('adType', AdType.REWARDED_CROSS_PROMO);
                return crossPromoShowable.loadAsync();
            }
        },
        showAsync: async () => {
            if (crossPromoShowable !== undefined) {
                track('adType', AdType.REWARDED_CROSS_PROMO);
                return crossPromoShowable.showAsync();
            }

            try {
                if (platformAdsDisabled) throw new Error('Ad platform disabled.');
                console.log('Showing Facebook ad...');
                await facebookShowable.showAsync();
                track('adType', AdType.REWARDED);
            } catch (e) {
                console.log('Showing Facebook ad failed.');

                if (e && e.code === 'USER_INPUT') {
                    throw e;
                }

                // Ad shown event already sent by this point so no point in tracking that the fb ad failed
                console.warn(e);
                crossPromoShowable = await getCrossPromoShowable(state);
                track('adType', AdType.REWARDED_CROSS_PROMO);
                return crossPromoShowable.showAsync();
            }
        }
    };
}

async function getCrossPromoShowable(state: IGameState): Promise<IShowable> {
    console.log('Fetching X-Promo ad...');

    if (isOSBelowVersion('Android', 6)) return Promise.reject('cross-promo is not supported on Android 5 or below.');

    const campaign = await CrossPromoDriver.getCampaignOfType(CrossPromoPlacement.NO_REWARDED_ADS, state);
    if (!campaign) return Promise.reject('no cross-promo NO_REWARDED_ADS campaign found');

    const source = campaign.getAssetSource(CrossPromoAssetType.VIDEO);
    if (!source) return Promise.reject('no cross-promo NO_REWARDED_ADS campaign video found');

    // Override ad tracking.
    track('adType', AdType.REWARDED_CROSS_PROMO);


    // Duration set to 10 seconds.
    const duration = 1000 * 10;

    // Use a fallback image for rewarded ad cross-promo in iOS.
    if (isIOS()) {
        return getRewardedAdFallbackImage({
            source: `resource/graphics/rewarded_ad_fallback/rewarded_ad_fallback_${Random.int(1, 3)}.jpg`,
            duration,
            cta: Game.locale.getText('noXpromoFallback'),
        });
    }

    // Use the cross-promo video otherwise.
    return getCrossPromoRewardedVideoAsync({
        source,
        duration,
        action: () => isIOS() ? Promise.resolve() : switchGame(campaign.id),
        cta: Game.locale.getText('InviteCTA'),
    });
}